import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API, formatApiError, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Download, X, Loader2 } from "lucide-react";

const empty = { title_zh: "", title_en: "", desc_zh: "", desc_en: "", image: "", starts_at: "", location_zh: "", location_en: "", price: 0, capacity: 0, active: true };

function EventForm({ initial, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white";
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), capacity: Number(form.capacity) };
      if (initial?.id) await api.patch(`/admin/events/${initial.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-3" data-testid="event-form">
      <div className="flex justify-between"><h2 className="font-serif-cn text-xl text-[#003645] font-semibold">{initial?.id ? t("adminP3.events") : t("adminP3.events")}</h2><button type="button" onClick={onCancel}><X size={16} /></button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Title ZH" value={form.title_zh} onChange={(e) => setForm({ ...form, title_zh: e.target.value })} className={inputCls} data-testid="ef-title-zh" />
        <input required placeholder="Title EN" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputCls} data-testid="ef-title-en" />
        <textarea placeholder="Description ZH" value={form.desc_zh} onChange={(e) => setForm({ ...form, desc_zh: e.target.value })} className={inputCls} rows={3} />
        <textarea placeholder="Description EN" value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} className={inputCls} rows={3} />
        <input placeholder="Image URL" value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} />
        <input type="datetime-local" value={(form.starts_at || "").slice(0, 16)} onChange={(e) => setForm({ ...form, starts_at: new Date(e.target.value).toISOString() })} className={inputCls} data-testid="ef-starts-at" />
        <input placeholder="Location ZH" value={form.location_zh} onChange={(e) => setForm({ ...form, location_zh: e.target.value })} className={inputCls} />
        <input placeholder="Location EN" value={form.location_en} onChange={(e) => setForm({ ...form, location_en: e.target.value })} className={inputCls} />
        <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
        <input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={inputCls} />
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {t("common.active")}</label>
      <div className="flex gap-2 pt-2">
        <button disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="ef-save">
          {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 rounded-full border border-[#e5ebeb]">{t("common.cancel")}</button>
      </div>
    </form>
  );
}

export default function AdminEvents() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewingSignups, setViewingSignups] = useState(null);
  const [signups, setSignups] = useState([]);

  const load = () => api.get("/events", { params: { include_inactive: true } }).then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };

  const openSignups = async (ev) => {
    setViewingSignups(ev);
    const { data } = await api.get("/admin/event-signups", { params: { event_id: ev.id } });
    setSignups(data);
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/event-signups/${id}`, { status });
    toast.success(t("common.saved"));
    if (viewingSignups) openSignups(viewingSignups);
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("jl_token");
    const resp = await fetch(`${API}/admin/events/${viewingSignups.id}/signups.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `signups_${viewingSignups.id}.csv`;
    link.click();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-events-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP3.events")}</h1>
        {!editing && !viewingSignups && (
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645]" data-testid="new-event-btn">
            <Plus size={14} /> New
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-6"><EventForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></div>
      )}

      {viewingSignups && (
        <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="signups-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-serif-cn text-xl text-[#003645] font-semibold">{t("adminP3.signups")} — {viewingSignups.title_zh}</h2>
            <div className="flex gap-2">
              <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#005870] text-[#005870] text-sm hover:bg-[#005870] hover:text-white transition-colors duration-200" data-testid="csv-export-btn">
                <Download size={14} /> {t("adminP3.exportCsv")}
              </button>
              <button onClick={() => setViewingSignups(null)} className="p-1.5 rounded-full text-[#4a6e77]"><X size={14} /></button>
            </div>
          </div>
          <table className="mt-4 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-[#4a6e77]"><th className="py-2 border-b border-[#e5ebeb]">Name</th><th className="py-2 border-b border-[#e5ebeb]">Contact</th><th className="py-2 border-b border-[#e5ebeb]">Status</th><th className="py-2 border-b border-[#e5ebeb]"></th></tr></thead>
            <tbody>
              {signups.map((s) => (
                <tr key={s.id}>
                  <td className="py-2 border-b border-[#e5ebeb] text-[#003645]">{s.name}</td>
                  <td className="py-2 border-b border-[#e5ebeb] text-xs text-[#4a6e77]">{s.user_email}<br/>{s.phone}</td>
                  <td className="py-2 border-b border-[#e5ebeb] text-xs">{t(`events.statuses.${s.status}`)}</td>
                  <td className="py-2 border-b border-[#e5ebeb]">
                    {s.status !== "confirmed" && <button onClick={() => setStatus(s.id, "confirmed")} className="text-[#005870] hover:text-[#00868e] text-xs mr-2">Confirm</button>}
                    {s.status !== "rejected" && <button onClick={() => setStatus(s.id, "rejected")} className="text-red-700 text-xs">Reject</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!editing && !viewingSignups && (
        <div className="mt-6 space-y-3">
          {events.map((ev) => (
            <div key={ev.id} className="rounded-lg border border-[#e5ebeb] bg-white p-4 flex gap-4" data-testid={`admin-event-${ev.id}`}>
              {ev.image && <div className="h-16 w-24 rounded overflow-hidden bg-[#f5f0e8]"><img src={resolveImage(ev.image)} alt="" className="h-full w-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#003645]">{ev.title_zh}</div>
                <div className="text-xs text-[#4a6e77]">{new Date(ev.starts_at).toLocaleString()} · {ev.location_zh}</div>
              </div>
              <button onClick={() => openSignups(ev)} className="text-[#005870] hover:text-[#00868e] text-xs" data-testid={`view-signups-${ev.id}`}>Signups</button>
              <button onClick={() => setEditing(ev)} className="text-[#005870] hover:text-[#00868e]"><Edit size={14} /></button>
              <button onClick={() => del(ev.id)} className="text-red-700"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
